/**
 * 📤 Upload Service - Serviço Centralizado de Upload de Arquivos
 * 
 * Classe utilitária reutilizável para upload de imagens e documentos
 * para o Cloudflare R2 via backend.
 * 
 * @example
 * ```javascript
 * import UploadService from '../../services/UploadService';
 * 
 * const uploadService = new UploadService();
 * 
 * const handleUpload = async (file) => {
 *   try {
 *     const result = await uploadService.uploadImage(file);
 *     console.log('URL:', result.url);
 *   } catch (error) {
 *     console.error('Erro:', error.message);
 *   }
 * };
 * ```
 * 
 * @author Sistema Pagmus
 * @version 1.0.0
 * @date 2025-10-26
 */

import { API_URL } from '../config/api';

class UploadService {
  constructor() {
    // ✅ ENDPOINT CORRETO: /api/upload/isolated (mesmo usado no cadastro de produtos)
    this.endpoint = `${API_URL}/api/upload/isolated`;
    this.maxFileSize = 5 * 1024 * 1024; // 5MB
    this.validImageFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
    this.validDocumentFormats = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  }

  /**
   * Valida o arquivo antes do upload
   * 
   * @param {File} file - Arquivo a ser validado
   * @param {Object} options - Opções de validação
   * @returns {Object} - Resultado da validação
   */
  validateFile(file, options = {}) {
    const {
      maxSize = this.maxFileSize,
      allowedFormats = this.validImageFormats,
      type = 'image'
    } = options;

    // Verificar se arquivo existe
    if (!file) {
      return {
        valid: false,
        error: 'Nenhum arquivo fornecido'
      };
    }

    // Verificar tamanho
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
      return {
        valid: false,
        error: `Arquivo muito grande. Máximo permitido: ${maxSizeMB}MB`
      };
    }

    // Verificar formato
    if (!allowedFormats.includes(file.type)) {
      return {
        valid: false,
        error: `Formato não suportado. Use: ${allowedFormats.map(f => f.split('/')[1]).join(', ')}`
      };
    }

    return {
      valid: true,
      size: file.size,
      type: file.type,
      name: file.name
    };
  }

  /**
   * Cria preview local de uma imagem
   * 
   * @param {File} file - Arquivo de imagem
   * @returns {Promise<string>} - Data URL para preview
   */
  createPreview(file) {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('Nenhum arquivo fornecido'));
        return;
      }

      const reader = new FileReader();
      
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      
      reader.onerror = () => {
        reject(new Error('Erro ao ler arquivo'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * Faz upload de uma imagem para o servidor
   * 
   * @param {File} file - Arquivo de imagem
   * @param {Object} options - Opções de upload
   * @returns {Promise<Object>} - Resultado do upload
   */
  async uploadImage(file, options = {}) {
    try {
      console.log('📤 Iniciando upload de imagem:', file.name);

      // Validar arquivo
      const validation = this.validateFile(file, {
        type: 'image',
        allowedFormats: this.validImageFormats
      });

      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Preparar FormData
      const formData = new FormData();
      formData.append('image', file);

      // Token de autenticação
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token de autenticação não encontrado');
      }

      // Fazer upload
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      // Verificar resposta
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Erro ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || result.error || 'Erro no upload');
      }

      console.log('✅ Upload concluído:', result.data.url);

      // Callback de progresso (se fornecido)
      if (options.onProgress) {
        options.onProgress(100);
      }

      return result.data;

    } catch (error) {
      console.error('❌ Erro no upload:', error);
      throw error;
    }
  }

  /**
   * Faz upload de um documento para o servidor
   * 
   * @param {File} file - Arquivo de documento
   * @param {Object} options - Opções de upload
   * @returns {Promise<Object>} - Resultado do upload
   */
  async uploadDocument(file, options = {}) {
    try {
      console.log('📤 Iniciando upload de documento:', file.name);

      // Validar arquivo
      const validation = this.validateFile(file, {
        type: 'document',
        allowedFormats: this.validDocumentFormats
      });

      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Usar mesmo endpoint (backend identifica pelo MIME type)
      const formData = new FormData();
      formData.append('image', file); // Campo 'image' mesmo para documentos

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token de autenticação não encontrado');
      }

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Erro ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || result.error || 'Erro no upload');
      }

      console.log('✅ Upload de documento concluído:', result.data.url);

      return result.data;

    } catch (error) {
      console.error('❌ Erro no upload de documento:', error);
      throw error;
    }
  }

  /**
   * Upload com preview e progresso
   * 
   * @param {File} file - Arquivo a ser enviado
   * @param {Object} callbacks - Callbacks para preview e progresso
   * @returns {Promise<Object>} - Resultado do upload
   */
  async uploadWithPreview(file, callbacks = {}) {
    const {
      onPreview = null,
      onProgress = null,
      onSuccess = null,
      onError = null
    } = callbacks;

    try {
      // Criar preview
      if (onPreview) {
        const preview = await this.createPreview(file);
        onPreview(preview);
      }

      // Simular progresso inicial
      if (onProgress) {
        onProgress(10);
      }

      // Upload
      const result = await this.uploadImage(file, {
        onProgress: (percent) => {
          if (onProgress) {
            onProgress(percent);
          }
        }
      });

      // Callback de sucesso
      if (onSuccess) {
        onSuccess(result);
      }

      return result;

    } catch (error) {
      // Callback de erro
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }

  /**
   * Formata tamanho de arquivo para exibição
   * 
   * @param {number} bytes - Tamanho em bytes
   * @returns {string} - Tamanho formatado
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Verifica se um arquivo é uma imagem
   * 
   * @param {File} file - Arquivo a ser verificado
   * @returns {boolean} - Se é uma imagem
   */
  isImage(file) {
    return file && this.validImageFormats.includes(file.type);
  }

  /**
   * Verifica se um arquivo é um documento
   * 
   * @param {File} file - Arquivo a ser verificado
   * @returns {boolean} - Se é um documento
   */
  isDocument(file) {
    return file && this.validDocumentFormats.includes(file.type);
  }
}

// Exportar instância singleton
const uploadService = new UploadService();

export default uploadService;

// Também exportar a classe para casos onde é necessário criar múltiplas instâncias
export { UploadService };

